<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import { useBookingContextStore } from '~/stores/pos/bookingContext'

/**
 * Replaces the cart's "Generate order" while a booking context is active.
 * Sends the cart to PUT /bookings/:id/order — never creates a sale, never
 * takes money. Exiting leaves the booking as it was (created, total null).
 */
const { t } = useNuxtApp().$i18n
const commerceStore = useCommerceStore()
const { cart, cartTotal, itemsInCart, selectedDiscount, openCart } = storeToRefs(commerceStore)
const { clearCart } = commerceStore
const bookingContext = useBookingContextStore()
const { active } = storeToRefs(bookingContext)
const businessStore = useBusinessStore()
const { businessId } = storeToRefs(businessStore)
const { put, get } = useAuthenticatedFetch()
const confirm = useConfirm()

const saving = ref(false)
// A cita and a pedido share the cart flow; only the words differ.
const isCita = computed(() => active.value?.returnTo === '/store/pos/appointments')
const savedKey = computed(() => isCita.value ? 'appointment.productsSaved' : 'booking.productsSaved')
const canSave = computed(() => !!active.value && itemsInCart.value > 0 && !saving.value)

// The lock guards the CART, but every edit of the order (total, customer)
// bumps orders.version too. Re-sync when the cart comes into view so the
// user's own edits in the agenda don't read as a conflict; only what changes
// while they are here does.
async function refreshVersion(): Promise<number | null> {
  if (!active.value) return null
  try {
    const order = await get(`/api/orders/${active.value.orderId}`)
    const v = Number(order?.version)
    if (Number.isFinite(v)) bookingContext.setVersion(v)
    return Number.isFinite(v) ? v : null
  }
  catch {
    return null
  }
}
onMounted(() => {
  // A context left over from before the feature was switched off must not
  // keep hijacking the cart's checkout. A cita needs `appointments`, a
  // pedido needs `bookings`; a business can have either one alone.
  const needed = active.value?.returnTo === '/store/pos/appointments' ? 'appointments' : 'bookings'
  if (businessStore.businessInfo?.features?.[needed] !== true) return bookingContext.clear()
  refreshVersion()
})
watch(() => active.value?.bookingId, refreshVersion)

// Back to the agenda, on the booking's day, with its dialog open — whether
// the products were saved or not, that dialog is where the user came from.
function leave() {
  const target = active.value
  clearCart()
  bookingContext.clear()
  openCart.value = false
  const page = target?.returnTo ?? '/store/pos/bookings'
  navigateTo(target
    ? `${page}?open=${target.bookingId}&date=${target.date}`
    : page)
}

async function putCart(version: number) {
  if (!active.value || !businessId.value) return
  // Same cart shape order.post.ts consumes; the server maps it to order_details.
  await put(`/api/business/${businessId.value}/bookings/${active.value.bookingId}/order`, {
    cart: cart.value,
    total: Number(cartTotal.value),
    discount: selectedDiscount.value?.percentage ?? null,
    version,
  })
}

async function save() {
  if (!active.value || !businessId.value || !canSave.value) return
  saving.value = true
  try {
    await putCart(active.value.version)
    useSuccessToast(savedKey.value)
    leave()
  }
  catch (e: unknown) {
    const status = (e as { statusCode?: number, status?: number })?.statusCode ?? (e as { status?: number })?.status
    if (status === 409) return askOverwrite()
    useErrorToast((e as { data?: { statusMessage?: string } })?.data?.statusMessage || 'booking.error')
  }
  finally {
    saving.value = false
  }
}

// Optimistic lock UX: say what happened, let the user decide, retry once
// with the version the server has now.
function askOverwrite() {
  confirm.require({
    message: t('booking.cart.conflict'),
    header: t(isCita.value ? 'appointment.cart.save' : 'booking.cart.save'),
    acceptLabel: t('booking.cart.overwrite'),
    rejectLabel: t('Cancel'),
    accept: async () => {
      const v = await refreshVersion()
      if (v == null) return useErrorToast('booking.error')
      saving.value = true
      try {
        await putCart(v)
        useSuccessToast(savedKey.value)
        leave()
      }
      catch (e: unknown) {
        useErrorToast((e as { data?: { statusMessage?: string } })?.data?.statusMessage || 'booking.error')
      }
      finally {
        saving.value = false
      }
    },
  })
}
</script>

<template>
  <div
    v-if="active"
    class="flex flex-col gap-2"
  >
    <Button
      :disabled="!canSave"
      :loading="saving"
      :label="t(isCita ? 'appointment.cart.save' : 'booking.cart.save')"
      icon="pi pi-calendar-plus"
      severity="primary"
      class="w-full text-base font-semibold py-2"
      @click="save"
    />
    <Button
      :label="t('booking.cart.leave')"
      icon="pi pi-times"
      severity="secondary"
      text
      class="w-full mb-4"
      @click="leave"
    />
  </div>
</template>
